import { assertNever, localStorageGetTyped, RegrelloLocalStorageKey } from "@regrello/core-utils";
import { useCallback, useMemo, useState, useTransition } from "react";
import { useMount } from "react-use";
import { RegrelloRestApiService } from "../../../../services/RegrelloRestApiService";
import { RouteQueryStringKeys } from "../../../../ui2/app/routes/consts";
import { useQueryMap } from "../../../../utils/hooks/useQueryStrings";
import {
  parseJwtClaim,
  type RegrelloGranularAccessJWTClaim,
  RegrelloGranularAccessJWTClaimKeys,
} from "../../../../utils/jwtUtil";
import { UnauthenticatedPageLayout } from "../../../auth/unauthenticatedPages/_internal/UnauthenticatedPageLayout";
import { PersonalizedLogo } from "../_internal/PersonalizedLogo";
import { ConfirmError } from "./_internal/ConfirmError";
import { NeedsConfirmation } from "./_internal/NeedsConfirmation";
import { Success } from "./_internal/Success";
import { Verifying } from "./_internal/Verifying";

type State = "PENDING" | "NEEDS_CONFIRMATION" | "ERROR" | "DOC_SUCCESS";

export function ConfirmVerification() {
  const [isLoading, startTransition] = useTransition();
  const { [RouteQueryStringKeys.VERIFICATION_NONCE]: nonce, [RouteQueryStringKeys.TOKEN]: granularAccessToken } =
    useQueryMap();

  const emailFromJWT = useMemo(() => {
    if (granularAccessToken == null) {
      return "";
    }
    const jwtClaim = parseJwtClaim<RegrelloGranularAccessJWTClaim>(granularAccessToken);
    return jwtClaim[RegrelloGranularAccessJWTClaimKeys.userEmail] ?? "";
  }, [granularAccessToken]);

  const [state, setState] = useState<State>(emailFromJWT !== "" ? "PENDING" : "ERROR");

  const exchangeNonceForDeviceVerificationToken = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      // In case this function is invoked from a form, we need to prevent the default `send` behavior.
      e?.preventDefault();
      startTransition(async () => {
        // This try catch is necessary because we need to catch json parsing errors.
        try {
          const response = await RegrelloRestApiService.exchangeVerificationLink(
            granularAccessToken,
            nonce,
            state === "NEEDS_CONFIRMATION",
          );
          if (response.status === 200) {
            window.location.replace(response.json.redirect);
            if (response.json.redirect.includes("/files/")) {
              setState("DOC_SUCCESS");
            }
            return;
          }
          setState("ERROR");
        } catch {
          setState("ERROR");
        }
      });
    },
    [granularAccessToken, nonce, state],
  );

  const content = useMemo(() => {
    switch (state) {
      case "PENDING":
        return <Verifying />;
      case "NEEDS_CONFIRMATION":
        return (
          <NeedsConfirmation
            email={emailFromJWT}
            isLoading={isLoading}
            onConfirm={exchangeNonceForDeviceVerificationToken}
          />
        );
      case "ERROR":
        return <ConfirmError />;
      case "DOC_SUCCESS":
        return <Success />;
      default:
        assertNever(state);
    }
  }, [emailFromJWT, exchangeNonceForDeviceVerificationToken, isLoading, state]);

  useMount(() => {
    const verificationTimestamp = localStorageGetTyped(RegrelloLocalStorageKey.VERIFICATION_TIMESTAMP);
    // If we do not have a verification timestamp, or it is older than 1 hour, we need confirmation click from user.
    // Verification timestamp would not be present if bot clicks the link, so we do not want to burn the `nonce`.
    if (verificationTimestamp == null || Number(verificationTimestamp) < Date.now() - 1000 * 60 * 60) {
      setState("NEEDS_CONFIRMATION");
      return;
    }

    exchangeNonceForDeviceVerificationToken();
  });

  return (
    <UnauthenticatedPageLayout>
      <div className="p-10 w-160">
        <header className="flex justify-between items-center p-4">
          <PersonalizedLogo granularAccessToken={granularAccessToken} />
        </header>
        <hr />

        {content}
      </div>
    </UnauthenticatedPageLayout>
  );
}
